// imports
import React, { FC, useState } from 'react';
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
// components
import { Alert } from '../../../shared/lib/common/Alert';
// interfaces
import { WalletPaymentProps } from '../app/interfaceTypes';
// graphql
import { useConfirmWalletPaymentMutation } from '../generated';
import { LABEL_WALLET_PAYMENT, BUTTON_TEXT_CLOSE } from '../../../shared/lib/constants';
// context
import { useAppContext } from '../app/contexts/AppContext';

const WalletPaymentModal: FC<WalletPaymentProps> = ({ open, onClose, clientSecret, gotoNext }) => {
  const elements = useElements();
  const stripe = useStripe();
  const [loading, setLoading] = useState<boolean>(true);

  const { setCurrentUser, currentUser } = useAppContext();

  const [confirmWalletPayment] = useConfirmWalletPaymentMutation({
    onCompleted: (data) => {
      const { confirmWalletPayment } = data;
      const { customerStripeId } = confirmWalletPayment || {};
      currentUser && setCurrentUser({ ...currentUser, isSubscribed: { customerStripeId } });
      gotoNext();
      onClose();
    }
  });

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>{LABEL_WALLET_PAYMENT}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        )}
        <ExpressCheckoutElement
          onReady={() => {
            setLoading(false);
          }}
          onConfirm={async () => {
            if (elements && stripe?.confirmPayment) {
              const { error } = await elements.submit();

              if (error) {
                Alert.error(error?.message || '');
              }

              const { error: intentError, paymentIntent } = await stripe.confirmPayment({ elements, clientSecret, confirmParams: {}, redirect: 'if_required' });

              if (intentError) {
                Alert.error(intentError?.message || '');
              }

              await confirmWalletPayment({ variables: { confirmWalletPaymentInput: { paymentIntentId: paymentIntent?.id || '' } } });
            }
          }}
          options={{
            paymentMethods: { googlePay: 'always', applePay: 'always', amazonPay: 'never', klarna: 'never', link: 'never', paypal: 'never' },
            buttonType: { googlePay: 'pay' }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button size="small" variant="outlined" onClick={onClose}>
          {BUTTON_TEXT_CLOSE}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WalletPaymentModal;
